import { CubeSaveGenericServices } from "./api-connector";
import { CONSOLE_WARN_BOLD, QueryStringManager, ResponseHandler } from "@green24/npm-javascript-utils";
import { ServiceSimulator } from "./ServiceSimulator";
import { E_Box_OpenMode } from "../models/constants/Enums_Storage";
import { M_Session } from "../models/Models_Session";
import { AppUtils } from "../utils/AppUtils";
import { E_Session_State } from "../models/constants/Enums_Session";

const simulatorSessionData = {
	...M_Session,
	id: -69,
	service: {
		physicalReference: "T69",
	}
}

export class SessionServices extends CubeSaveGenericServices {
	get createURL() {
		return this.url + '/';
	}

	getItem(itemID, processor = data => data, signal, additionalConfiguration) {
		return super.getItem(itemID, processor, signal, {
			batch: true,
			...additionalConfiguration,
		});
	}

	/**
	 * Open box with PIN
	 * ---
	 * @param {string} pin
	 * @param {E_Box_OpenMode} openMode Open mode
	 * @param {false|"resolve"|"reject"} simulateOpening Debug simulation of the opening process (To simulate the states, use either "resolve" or "reject")
	 * @return {Promise<Response, HttpResponseError>|ServiceSimulator}
	 */
	openBoxByPin(pin, openMode = E_Box_OpenMode.OPEN_DELAYED, simulateOpening = false) {
		if(AppUtils.isDev && simulateOpening) {
			console.log("%cSimulating the box opening process...", CONSOLE_WARN_BOLD);

			this._boxOpeningProcessSimulator = new ServiceSimulator(
				{
					resolved: {session: simulatorSessionData, success: true, additionalPaymentAmount: 0, openDelaySeconds: 10},
					rejected: {error: "TEST", statusCode: -1, errorMessage: "Simulated error"},
				},
				simulateOpening == "resolve" || simulateOpening === true,
				2000,
				() => this._boxOpeningProcessSimulator = null
			);
			return this._boxOpeningProcessSimulator;
		}

		return this.sendRequest({
			url: `${this.url}/open${new QueryStringManager({pin}).toValidDataString()}`,
			method: "POST",
		});
	}

	/**
	 * @override
	 * @inheritDoc
	 * @param {M_Session} sessionData
	 * @param {"resolve"|"reject"|undefined} simulateCreation
	 * @return {Promise<Response>|ServiceSimulator}
	 */
	createItem(sessionData, simulateCreation) {
		if(AppUtils.isDev && simulateCreation) {
			console.log("%cSimulating the session creation process...", CONSOLE_WARN_BOLD);

			this._sessionCreationProcessSimulator = new ServiceSimulator(
				{
					resolved: simulatorSessionData,
					rejected: {error: "TEST", statusCode: -1, errorMessage: "Simulated error"},
				},
				simulateCreation == "resolve" || simulateCreation === true,
				2000,
				() => this._sessionCreationProcessSimulator = null
			);
			return this._sessionCreationProcessSimulator;
		}

		return super.createItem(sessionData);
	}

	getPaymentSocketUrl(sessionID) {
		return `/session/${sessionID}/payment/terminal`;
	}

	/**
	 * Start payment
	 * ---
	 * @param {number} sessionID
	 * @param {"cs"|string} language
	 * @param {E_Payment_Mode} mode
	 * @return {Promise<Response>|ServiceSimulator}
	 */
	startPayment(sessionID, language = "cs", mode = "INITIAL_PAYMENT") {
		return this.sendRequest({
			url: `${this.url}/${sessionID}/payment/terminal${new QueryStringManager({language, mode}).toString()}`,
			method: "POST",
		}, ResponseHandler.plain);
	}

	simulateStartPayment(simulateProcess) {
		console.log("%cSimulating the payment process...", CONSOLE_WARN_BOLD);

		this._sessionPaymentProcessSimulator = new ServiceSimulator(
			{
				resolved: simulatorSessionData,
				rejected: {error: "TEST", statusCode: -1, errorMessage: "Simulated error"},
				aborted: {...simulatorSessionData, state: E_Session_State.CANCELED, paymentState: null},
			},
			simulateProcess == "resolve" || simulateProcess === true,
			2000,
			() => this._sessionPaymentProcessSimulator = null
		);
		return this._sessionPaymentProcessSimulator;
	}

	cancelPayment(sessionID) {
		if(AppUtils.isDev && this._sessionPaymentProcessSimulator) {
			console.log("%cAborting the simulated payment process", CONSOLE_WARN_BOLD);
			this._sessionPaymentProcessSimulator.abort(true);

			return Promise.resolve();
		}

		return this.sendRequest({
			url: `${this.url}/${sessionID}/payment/terminal`,
			method: "DELETE",
		}, ResponseHandler.plain);
	}

	/**
	 * Complete session with pin
	 * ---
	 *  @param {string} pin
	 * @param {E_Box_OpenMode} openMode Open mode
	 * @param {false|"resolve"|"reject"} simulateOpening Debug simulation of the opening process (To simulate the states, use either "resolve" or "reject")
	 * @return {Promise<Response, HttpResponseError>|ServiceSimulator}
	 */
	completeSessionWithPin(pin, openMode = E_Box_OpenMode.OPEN_DELAYED, simulateOpening = false) {
		if(AppUtils.isDev && simulateOpening) {
			console.log("%cSimulating the box opening process...", CONSOLE_WARN_BOLD);

			this._boxOpeningProcessSimulator = new ServiceSimulator(
				{
					resolved: {session: simulatorSessionData, success: true, additionalPaymentAmount: 0, openDelaySeconds: 10},
					rejected: {error: "TEST", statusCode: -1, errorMessage: "Simulated error"},
				},
				simulateOpening == "resolve" || simulateOpening === true,
				2000,
				() => this._boxOpeningProcessSimulator = null
			);
			return this._boxOpeningProcessSimulator;
		}

		return this.sendRequest({
			url: `${this.url}/complete${new QueryStringManager({pin, openMode}).toValidDataString()}`,
			method: "POST",
		});
	}

	submitDelivery(body) {
		return this.sendRequest({
			url:`${this.url}/delivery/`,
			method: "POST",
			body: JSON.stringify(body),
		})
	}
}

export const sessionServices = new SessionServices(`/session`);
